import { useCallback, useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Circle, ChevronRight, Compass, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/thesis`;

const STEP_COPY = {
  setup: {
    title: 'Describe the work',
    hint: 'Title, research question and document type — everything the AI writes is steered by these.',
    cta: 'Open settings',
  },
  evidence: {
    title: 'Load the evidence',
    hint: 'Upload at least 3 sources (PDF/DOCX/MD) or import from PubMed. Nothing is generated without sources.',
    cta: 'Add sources',
  },
  guideline: {
    title: 'Pick a reporting standard',
    hint: 'PRISMA, CONSORT, STROBE… the checklist then drives compliance and readiness checks.',
    cta: 'Choose guideline',
  },
  structure: {
    title: 'Build the structure',
    hint: 'Let the AI suggest the sections for your document type, then reorder or rename them.',
    cta: 'Create sections',
  },
  code: {
    title: 'Attach a Python project (optional)',
    hint: 'For software/methods papers: connect GitHub or a ZIP, pick a component, and get Methods text with file-level provenance.',
    cta: 'Open Python project',
  },
  write: {
    title: 'Write the sections',
    hint: 'Generate section by section, then refine with Smart Continue and the selection toolbar.',
    cta: 'Start writing',
  },
  visuals: {
    title: 'Add figures or tables',
    hint: 'Upload images or save numbered tables — captions and references renumber themselves.',
    cta: 'Open figures & tables',
  },
  statistics: {
    title: 'Upload your data',
    hint: 'Turn a CSV into Table 1, survival curves, ROC analysis and regression tables.',
    cta: 'Open data & statistics',
  },
  verify: {
    title: 'Verify before you submit',
    hint: 'Readiness score, claim grounding, reviewer simulation and plagiarism scan — run them per section.',
    cta: 'Run checks',
  },
  cite: {
    title: 'Complete the citations',
    hint: 'Every claim needs a source. Citation tokens (@pmid:/@doi:) become the numbered bibliography.',
    cta: 'Manage sources',
  },
  export: {
    title: 'Export the manuscript',
    hint: 'DOCX, PDF, HTML or Markdown with the AI-assistance disclosure and the full bibliography.',
    cta: 'Export',
  },
};

export const GettingStartedPanel = ({ userId, projectId, onGoToTab }) => {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/getting-started`,
        { params: { user_id: userId } });
      setData(res.data);
      setHidden(res.data.dismissed);
    } catch (e) {
      setData(null);
    }
  }, [userId, projectId]);

  useEffect(() => { load(); }, [load]);

  const dismiss = async () => {
    setHidden(true);
    try {
      await axios.post(`${API}/projects/${projectId}/getting-started/dismiss`, {},
        { params: { user_id: userId, dismissed: true } });
    } catch (e) { /* non-blocking */ }
  };

  if (!data) return null;

  if (hidden) {
    return (
      <Button variant="outline" size="sm" onClick={() => setHidden(false)}
        data-testid="getting-started-reopen">
        <Compass className="w-4 h-4 mr-2" />Start here ({data.progress_pct}%)
      </Button>
    );
  }

  return (
    <Card className="border-primary/30" data-testid="getting-started-panel">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base md:text-lg">
              <Compass className="w-4 h-4" />Start here — your path to a submittable manuscript
            </CardTitle>
            <CardDescription className="mt-1">
              {data.done_count} of {data.total} steps done. Each step opens the right panel — you never
              have to leave the app to finish the manuscript.
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={dismiss} data-testid="getting-started-dismiss">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden mt-3">
          <div className="h-full bg-primary transition-all duration-500"
            style={{ width: `${data.progress_pct}%` }} data-testid="getting-started-progress" />
        </div>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {data.steps.map((s) => {
          const copy = STEP_COPY[s.key] || { title: s.key, hint: '', cta: 'Open' };
          const isNext = data.next_step === s.key;
          return (
            <div key={s.key}
              className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${
                isNext ? 'border-primary bg-primary/5' : 'border-transparent hover:bg-muted/40'}`}
              data-testid={`getting-started-step-${s.key}`}>
              {s.done
                ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />
                : <Circle className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-sm font-medium ${s.done ? 'text-muted-foreground line-through' : ''}`}>
                    {copy.title}
                  </span>
                  {s.optional && <Badge variant="outline" className="text-[10px]">optional</Badge>}
                  {isNext && <Badge variant="outline" className="text-[10px] bg-primary/10 border-primary/30">next</Badge>}
                  {s.count > 0 && <span className="text-[11px] text-muted-foreground">{s.count}</span>}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 break-words">{copy.hint}</p>
              </div>
              <Button size="sm" variant={isNext ? 'default' : 'outline'}
                onClick={() => onGoToTab?.(s.tab)} data-testid={`getting-started-go-${s.key}`}>
                {copy.cta}<ChevronRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default GettingStartedPanel;
